import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '@speico-app/shared';
import api from '../services/api';
import { config } from '../config';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithMicrosoft: () => void;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('accessToken'));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      // Check for Microsoft OAuth callback parameters
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get('accessToken');
      
      if (accessToken) {
        try {
          // Store the token
          localStorage.setItem('accessToken', accessToken);
          setToken(accessToken);
          
          // Always fetch fresh user data from the API to ensure we have the latest role
          const response = await api.get('/api/auth/me');
          setUser(response.data.data);
          
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          
          setIsLoading(false);
          return;
        } catch (err) {
          console.error('Error fetching user data after OAuth:', err);
          localStorage.removeItem('accessToken');
          setToken(null);
        }
      }
      
      // Check for existing token
      const existingToken = localStorage.getItem('accessToken');
      if (existingToken) {
        try {
          const response = await api.get('/api/auth/me');
          setUser(response.data.data);
          setToken(existingToken);
        } catch (err) {
          localStorage.removeItem('accessToken');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []); // Remove token dependency to prevent infinite loop

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.post('/api/auth/login', { email, password });
      const { accessToken, user: userData } = response.data.data;
      
      localStorage.setItem('accessToken', accessToken);
      setToken(accessToken);
      setUser(userData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithMicrosoft = () => {
    // Redirect to Microsoft OAuth endpoint on the backend
    window.location.href = `${config.apiUrl}/api/auth/microsoft`;
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    setToken(null);
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    loginWithMicrosoft,
    logout,
    isLoading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
